import { useMemo, useState } from 'react';
import { merge } from 'lodash';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { Url } from '@rossum/api-client';
import {
  QueueAction,
  QueueRule,
  SchemaSection,
} from '@rossum/api-client/schemas';
import { ColumnHeaderPaper } from '@rossum/components/data-grid/ColumnHeaderPaper';
import { commonDataGridStyles } from '@rossum/components/data-grid/styles';
import { Stack } from '@rossum/ui/material';
import {
  DataGridPro,
  gridClasses,
  GridPaginationModel,
} from '@rossum/ui/x-data-grid-pro';
import { ValidatedGridFilterItem } from '../../document-list-filtering/types';
import { ContentForPreview } from './hooks/makeArtificialContent';
import { useCalculatePreview } from './hooks/useCalculatePreview';
import { useCalculateRulesPreview } from './hooks/useCalculateRulesPreview';
import { useColumns } from './hooks/useColumns';
import { useUpdateColumWidth } from './hooks/useColumnWidths';
import {
  DEFAULT_PAGE_SIZE,
  useGetAnnotationsForPreview,
} from './hooks/useGetAnnotationsForPreview';
import {
  actionsColumn,
  conditionsColumn,
  documentsColumn,
} from './rows/helpers';
import { useRows } from './rows/useRows';

const PAGE_SIZES = [DEFAULT_PAGE_SIZE, 10, 15, 20];

// Use lodash instead of remeda for performance reasons
const gridStyles = merge(
  { ...commonDataGridStyles },
  {
    [`.${gridClasses.cell}`]: {
      py: 1,
    },
  }
);

export type FormulaPreviewGridProps = {
  queueUrls: Url[];
  currentFormulaId: string;
  schemaForPreview: SchemaSection[];
  schemaRules?: Partial<QueueRule>[];
  type: 'rule' | 'formula';
  ActionComponent?: React.FC<{ action: QueueAction }> | undefined;
  filters: ValidatedGridFilterItem[];
  queueName?: string;
  pageSizeOptionsOverride?: number[];
};

export const FormulaPreviewGrid = ({
  queueUrls,
  currentFormulaId,
  schemaForPreview,
  schemaRules,
  type,
  ActionComponent,
  filters,
  queueName,
  pageSizeOptionsOverride,
}: FormulaPreviewGridProps) => {
  const intl = useIntl();

  const pageSizeOptions = pageSizeOptionsOverride ?? PAGE_SIZES;

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: DEFAULT_PAGE_SIZE,
    page: 0,
  });

  const annotationsQuery = useGetAnnotationsForPreview({
    queueUrls,
    pageSize: paginationModel.pageSize,
    filters,
  });

  const annotations = useMemo(
    () => annotationsQuery.data?.pages.flatMap(page => page.results) ?? [],
    [annotationsQuery.data?.pages]
  );

  const firstQueueUrl = queueUrls[0];

  const [dependencies, setDependencies] = useState<Record<string, string>>({});

  // Hide preview row if queue is not defined for whatever reason
  const previewRow = firstQueueUrl
    ? {
        type: 'from_dependencies' as const,
        queue: firstQueueUrl,
        schemaContent: schemaForPreview,
        dependencies,
      }
    : undefined;

  const testContent: ContentForPreview[] = [
    ...(previewRow ? [previewRow] : []),
    ...annotations.map(annotation => ({
      annotationUrl: annotation.url,
      annotationQueue: annotation.queue,
      type: 'from_annotation' as const,
    })),
  ];

  const evaluationQueriesFormula = useCalculatePreview({
    content: testContent,
    schemaContent: schemaForPreview,
    currentFormulaId,
    disabled: type === 'rule',
  });

  const evaluationQueriesRules = useCalculateRulesPreview(
    testContent,
    type === 'formula',
    schemaRules
  );

  const evaluationQueries =
    type === 'formula' ? evaluationQueriesFormula : evaluationQueriesRules;

  const columns = useColumns({
    currentFormulaId,
    schemaForPreview,
    type,
    ActionComponent,
    queueName,
  });

  const pageData = useMemo(
    () => annotationsQuery.data?.pages[paginationModel.page],
    [annotationsQuery.data?.pages, paginationModel.page]
  );

  const rows = useRows({
    showPreviewLine: true,
    pageSize: paginationModel.pageSize,
    columns,
    annotations: pageData?.results,
    documents: pageData?.documents,
    evaluationQueriesFormula:
      type === 'formula' ? evaluationQueriesFormula : undefined,
    evaluationQueriesRules:
      type === 'rule' ? evaluationQueriesRules : undefined,
  });

  const paginationNextIconButtonProps = useMemo(
    () => ({
      sx: annotationsQuery.isFetching
        ? {
            color: 'text.disabled',
            pointerEvents: 'none',
          }
        : {},
    }),
    [annotationsQuery.isFetching]
  );

  const updateColumnWidth = useUpdateColumWidth();

  return (
    <Stack
      spacing={2}
      sx={{
        background: theme => theme.palette.background.paper,
        mt: 2,
      }}
    >
      <DataGridPro
        disableRowSelectionExcludeModel
        slots={{
          columnHeaders: ColumnHeaderPaper,
        }}
        slotProps={{
          columnHeaders: {
            // @ts-expect-error
            elevation: 0,
          },
          loadingOverlay: {
            variant: 'linear-progress',
            color: 'secondary',
          },
          basePagination: {
            // @ts-expect-error wrong type
            nextIconButtonProps: paginationNextIconButtonProps,
            sx: {
              // hide pagination when there are no rows
              display: rows.length === 0 ? 'none' : 'inherit',
              justifyContent: 'flex-end',
              [`> * p`]: {
                margin: 0,
              },
            },
          },
        }}
        localeText={
          rows.length === 0
            ? {
                noRowsLabel: intl.formatMessage({
                  id: 'features.queueSettings.fields.formulaPreview.noAnnotations',
                }),
              }
            : undefined
        }
        autoHeight
        loading={
          annotationsQuery.isFetching ||
          evaluationQueries.some(query => query.isFetching)
        }
        columns={columns}
        // For mysterious reasons, the header is not 80px high but 50-ish
        // I don't know why, but i account for it here
        columnHeaderHeight={80}
        rows={rows}
        editMode="row"
        isCellEditable={params => params.row.annotationUrl === 'preview'}
        processRowUpdate={newRow => {
          // After editing, collect previews from newRow
          // Then update dependencies and re-evaluate the previews

          const rowModelField = R.pipe(
            newRow,
            R.omitBy((_, key) => !key.startsWith('columns.')),
            R.mapKeys(key => key.replace('columns.', '')),
            R.mapValues(newRowValue => {
              if (
                typeof newRowValue === 'object' &&
                newRowValue !== null &&
                'preview' in newRowValue
              ) {
                return newRowValue.preview;
              }
              return '';
            })
          );

          setDependencies(rowModelField);
          return newRow;
        }}
        onColumnWidthChange={updateColumnWidth}
        density="compact"
        disableColumnMenu
        disableColumnReorder
        pagination
        paginationModel={paginationModel}
        paginationMode="server"
        getRowHeight={() => 'auto'}
        onPaginationModelChange={newModel => {
          setPaginationModel(newModel);

          if (
            annotationsQuery.data &&
            annotationsQuery.data.pages.length <= newModel.page
          ) {
            annotationsQuery.fetchNextPage();
          }
        }}
        rowCount={annotationsQuery.data?.pages[0]?.pagination.total ?? 0}
        pageSizeOptions={pageSizeOptions}
        disableRowSelectionOnClick
        initialState={{
          pinnedColumns: {
            left: [documentsColumn, conditionsColumn, actionsColumn],
          },
        }}
        sx={{
          '&': {
            '--DataGrid-pinnedBackground': t => t.palette.background.paper,
            '--DataGrid-containerBackground': t => t.palette.background.paper,
          },
          ...gridStyles,
        }}
      />
    </Stack>
  );
};
